import React, { useEffect, useState } from "react";
import { Box, PageLayout, Text, ActionList, ActionMenu } from "@primer/react";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  SyncIcon,
  KebabHorizontalIcon,
} from "@primer/octicons-react";
import { Dialog } from "@primer/react/drafts";
import ThangDiemDetailModal from "./detail_modal";
import { useSelector } from "react-redux";
import { useCommonContext } from "../../../contexts/common";
import { NotifyHelper } from "../../../helpers/toast";
import { thangDiemApi } from "../../../api/thangDiemApi";
import { ex_thangdiem } from "../../../models/response/thang-diem/ex_thangdiem";
import MyButton from "../../../components/ui/button/Button";
import DataTable from "../../../components/ui/data-table";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";

const ThangDiem: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [thangDiems, setThangDiems] = useState<ex_thangdiem[]>([]);
  const [selectedThangDiem, setSelectedThangDiem] =
    useState<ex_thangdiem | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const { translate } = useCommonContext();
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();

  const loadThangDiems = async () => {
    setIsLoading(true);
    try {
      const res = await thangDiemApi.selectAll();
      if (res.is_success) {
        const filteredData = res.data.filter(
          (x: ex_thangdiem) => x.dm_coso_id === dm_coso_id
        );
        setThangDiems(filteredData);
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadThangDiems();
  }, [dm_coso_id]);

  const handleDelete = async (id: number) => {
    try {
      const res = await thangDiemApi.delete(id);
      if (res.is_success) {
        await loadThangDiems();
        NotifyHelper.Success("Xóa thành công");
      } else {
        NotifyHelper.Error(res.message || "Có lỗi");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi");
    }
  };

  return (
    <Box
      sx={{
        p: 3,
      }}
    >
      <DataTable
        height={`${window.innerHeight - 200}px`}
        data={thangDiems}
        isLoading={isLoading}
        title="Thang điểm"
        searchEnable
        paging={{ enable: true }}
        actionComponent={
          <Box sx={{ display: "flex" }}>
            <MyButton
              sx={{ ml: 1 }}
              text="Base.Label.AddNew"
              leadingVisual={PlusIcon}
              size="medium"
              variant="primary"
              onClick={() => {
                setSelectedThangDiem({ id: 0 } as ex_thangdiem);
                setShowDetailModal(true);
              }}
            />
            <MyButton
              text="Base.Label.Refresh"
              leadingVisual={SyncIcon}
              size="medium"
              sx={{ ml: 1 }}
              onClick={loadThangDiems}
            />
          </Box>
        }
        columns={[
          {
            id: "cmd",
            caption: "",
            width: "50px",
            align: "center",
            cellRender: (data: ex_thangdiem) => (
              <ActionMenu>
                <ActionMenu.Button
                  icon={KebabHorizontalIcon}
                  variant="invisible"
                  sx={{ m: -1 }}
                >
                  &nbsp;
                </ActionMenu.Button>
                <ActionMenu.Overlay width="auto">
                  <ActionList>
                    <ActionList.Item
                      onSelect={() => {
                        setSelectedThangDiem(data);
                        setShowDetailModal(true);
                      }}
                    >
                      <ActionList.LeadingVisual>
                        <PencilIcon />
                      </ActionList.LeadingVisual>
                      {translate("Base.Label.Edit")}
                    </ActionList.Item>
                    <ActionList.Item
                      variant="danger"
                      onSelect={() => handleDelete(data.id)}
                    >
                      <ActionList.LeadingVisual>
                        <TrashIcon />
                      </ActionList.LeadingVisual>
                      {translate("Base.Label.Delete")}
                    </ActionList.Item>
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            ),
          },
          {
            dataField: "thang_diem",
            caption: translate("Thang điểm"),
          },
          {
            dataField: "thang_diem_en",
            caption: translate("Thang điểm (En)"),
          },
          {
            dataField: "diem_toi_da",
            caption: translate("Điểm tối đa"),
          },
          {
            dataField: "free_text",
            caption: translate("Dạng tự do"),
            width: "100px", // Added fixed width for consistency
            align: "center", // Center alignment for checkbox
            cellRender: (data: ex_thangdiem) => (
              <input
                type="checkbox"
                checked={data.free_text}
                readOnly
                style={{ cursor: 'default' }}
              />
            )
          }
        ]}
      />

      {showDetailModal && selectedThangDiem && (
        <ThangDiemDetailModal
          id={selectedThangDiem.id}
          onCancel={() => setShowDetailModal(false)}
          onSuccess={() => {
            setShowDetailModal(false);
            loadThangDiems();
          }}
        />
      )}
    </Box>
  );
};

export default ThangDiem;
